import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, Notification, NotificationResponse } from '../lib/api/notifications';
import { useAuth } from '@/components/AuthProvider';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useChat } from '@/contexts/ChatContext';
import { useToast } from '@/hooks/use-toast';
import { CURRENT_SUBSCRIPTION_KEY } from '@/hooks/useCurrentSubscription';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  /**
   * Bulk-mark all MESSAGE_RECEIVED notifications for a conversation as read LOCALLY
   * (no per-notification PUT calls). Pairs with UnreadMessagesContext.markConversationAsRead,
   * which already performs the server-side bulk mark via MessageController.
   */
  markConversationAsReadLocal: (conversationId: string) => void;
  refetch: () => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  // Safely get business account context (may be undefined for admin/agency routes)
  const businessAccountContext = useContext(BusinessAccountContext);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsList, setNotificationsList] = useState<Notification[]>([]);
  const { socket, isConnected } = useChat();
  const currentScope = selectedAccountId || 'no-account';
  const cacheKey = useMemo<[string, string]>(() => ['notifications', currentScope], [currentScope]);
  const businessRoomRef = useRef<string | null>(null);
  const latestScopeRef = useRef(currentScope);
  const seenTemplateStatusEventsRef = useRef<Map<string, number>>(new Map());
  // Dedupe key for the generic real-time toast — same notification id within a
  // short window (rare socket double-emit / reconnection replay) should only
  // toast once. Keyed by notification id with a recent-timestamp value.
  const toastedNotificationIdsRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    latestScopeRef.current = currentScope;
  }, [currentScope]);

  if (false && !user) {
    const emptyContext: NotificationContextType = {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      deleteNotification: async () => {},
      markConversationAsReadLocal: () => {},
      refetch: () => {},
    };

    return (
      <NotificationContext.Provider value={emptyContext}>
        {children}
      </NotificationContext.Provider>
    );
  }

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery<NotificationResponse>({
    queryKey: cacheKey,
    queryFn: () =>
      notificationsApi.getAll({
        businessAccountId: selectedAccountId || undefined,
        orderBy: 'created_at',
        ascending: false,
        limit: 50,
      }),
    enabled: !!user,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (notificationsData?.data) {
      setNotificationsList(notificationsData.data);
      setUnreadCount(notificationsData.unreadCount || 0);
    } else if (!notificationsData && !isLoading) {
      setNotificationsList([]);
      setUnreadCount(0);
    }
  }, [notificationsData, isLoading]);

  // Ref mirror of notificationsList so markAsRead can short-circuit on already-read
  // entries without taking `notificationsList` as a useCallback dep (which would
  // re-create the callback on every list change and cascade into consumers' effects).
  const notificationsListRef = useRef<Notification[]>([]);
  useEffect(() => {
    notificationsListRef.current = notificationsList;
  }, [notificationsList]);

  const mutateCache = useCallback(
    (
      scope: string | null | undefined,
      updater: (oldData?: NotificationResponse) => NotificationResponse | undefined
    ) => {
      const key: [string, string] = ['notifications', scope || 'no-account'];
      queryClient.setQueryData(key, (oldData?: NotificationResponse) => updater(oldData));
    },
    [queryClient]
  );

  const normalizeNotificationPayload = useCallback(
    (payload: any): Notification => {
      const createdAt = payload?.createdAt || payload?.created_at || new Date().toISOString();
      const updatedAt = payload?.updatedAt || payload?.updated_at || createdAt;
      const businessAccountId =
        payload?.businessAccountId ??
        payload?.business_account_id ??
        payload?.businessAccountID ??
        null;

      return {
        id:
          payload?.id ||
          payload?.notificationId ||
          payload?.notification_id ||
          payload?.messageId ||
          `notification-${Date.now()}-${Math.random()}`,
        type: payload?.type || payload?.notificationType || 'GENERAL',
        title: payload?.title || payload?.message || 'Notification',
        message: payload?.message || payload?.body || '',
        data: payload?.data ?? payload?.metadata ?? null,
        isRead: payload?.isRead ?? payload?.is_read ?? false,
        createdAt,
        updatedAt,
        businessAccountId,
        organizationId: payload?.organizationId ?? payload?.organization_id ?? null,
        userId: payload?.userId ?? payload?.user_id ?? user?.id,
      };
    },
    [user?.id]
  );

  useEffect(() => {
    if (!socket || !isConnected || !user) return;

    const handleNotification = (payload: any) => {
      const normalized = normalizeNotificationPayload(payload);
      // Org-wide notifications (no businessAccountId — scheduled-post
      // completions, Sonny items, system events) belong to EVERY scope, so
      // land them in the scope the user is currently viewing; otherwise they
      // were cached under 'no-account' and never appeared in the bell while a
      // business account was selected. BA-scoped notifications keep their own
      // scope (mirrors the backend fetch filter, which also returns org-wide
      // rows under any BA scope).
      const targetScope = normalized.businessAccountId || latestScopeRef.current;

      mutateCache(targetScope, (oldData) => {
        const existingNotifications = oldData?.data || [];
        const updatedData = [
          normalized,
          ...existingNotifications.filter((notification) => notification.id !== normalized.id),
        ].slice(0, 50);

        if (!oldData) {
          return {
            success: true,
            data: updatedData,
            unreadCount: normalized.isRead ? 0 : 1,
          };
        }

        return {
          ...oldData,
          data: updatedData,
        };
      });

      if (targetScope === latestScopeRef.current) {
        setNotificationsList((prev) => {
          const updated = [
            normalized,
            ...prev.filter((notification) => notification.id !== normalized.id),
          ].slice(0, 50);
          return updated;
        });

        if (!normalized.isRead) {
          setUnreadCount((prev) => prev + 1);
        }
      }

      // Generic real-time toast for completion-style notifications. The bell
      // is passive — a toast closes the loop on async actions the user kicked
      // off (sync, import, export, campaign send) the same way the "Sync
      // Started" toast opened it. Centralised here so every existing/future
      // notification type benefits without per-feature wiring.
      //
      // Skip rules:
      //  - Noisy types that already have their own UI (inbox, chat, etc.)
      //  - Cross-account events when a business-scoped notification arrives
      //    for an account the user is not currently viewing (matches the
      //    existing template_status_update scoping above)
      //  - Duplicates within a 5-second window (handles rare socket replay)
      const NOISY_TOAST_SKIP_TYPES = new Set<string>([
        'MESSAGE_RECEIVED',
        'CONVERSATION_UPDATED',
        'NEW_MESSAGE',
        'TYPING',
      ]);
      const shouldToast = (() => {
        if (!normalized.title && !normalized.message) return false;
        if (NOISY_TOAST_SKIP_TYPES.has(String(normalized.type).toUpperCase())) {
          return false;
        }
        // Cross-account guard: if the event is scoped to a business account,
        // only toast when the user is currently viewing it. Org-wide events
        // (no businessAccountId) always toast.
        if (normalized.businessAccountId && targetScope !== latestScopeRef.current) {
          return false;
        }
        // Dedupe within 5s on notification id
        const now = Date.now();
        const lastSeen = toastedNotificationIdsRef.current.get(normalized.id) || 0;
        if (now - lastSeen < 5000) return false;
        toastedNotificationIdsRef.current.set(normalized.id, now);
        // Keep the dedupe map bounded
        if (toastedNotificationIdsRef.current.size > 250) {
          toastedNotificationIdsRef.current.clear();
        }
        return true;
      })();

      if (shouldToast) {
        const rawType = String(normalized.type || '').toLowerCase();
        const isFailure =
          rawType === 'error' ||
          rawType.endsWith('_failed') ||
          rawType.includes('failure');
        toast({
          title: normalized.title,
          description: normalized.message || undefined,
          variant: isFailure ? 'destructive' : 'default',
        });
      }

      // Fan-out a window event so feature panels (e.g. ContactRemindersPanel)
      // can re-fetch their own data when a relevant notification arrives.
      // Without this the reminders panel stays on `SCHEDULED` after the
      // reminder actually fires server-side (only refreshes on mount / 30s
      // poll), which looked like a real-time bug to the user on 2026-05-12.
      try {
        window.dispatchEvent(
          new CustomEvent('wenext:notification-received', {
            detail: {
              type: normalized.type,
              data: normalized.data,
              contactId:
                (normalized.data as any)?.contactId ||
                (normalized.data as any)?.contact_id ||
                null,
              reminderId:
                (normalized.data as any)?.reminderId ||
                (normalized.data as any)?.reminder_id ||
                null,
            },
          })
        );
      } catch {
        // CustomEvent unavailable in some test environments — non-critical.
      }
    };

    const handleTemplateStatusUpdate = (payload: any) => {
      const businessAccountId =
        payload?.businessAccountId ?? payload?.business_account_id ?? payload?.businessAccountID ?? null;
      const status = payload?.status;

      // Only show popups for current scope (prevents noise when switching accounts)
      const targetScope = businessAccountId || 'no-account';
      if (targetScope !== latestScopeRef.current) return;

      // Only requested: popup on approval
      if (status !== 'APPROVED') return;

      const templateId = payload?.templateId ?? payload?.template_id ?? '';
      const templateName = payload?.templateName ?? payload?.template_name ?? templateId ?? 'template';
      const dedupeKey = [targetScope, templateId, status].filter(Boolean).join(':');
      const now = Date.now();
      const lastSeen = seenTemplateStatusEventsRef.current.get(dedupeKey) || 0;
      if (now - lastSeen < 1500) return;
      seenTemplateStatusEventsRef.current.set(dedupeKey, now);

      // Keep map from growing unbounded
      if (seenTemplateStatusEventsRef.current.size > 250) {
        seenTemplateStatusEventsRef.current.clear();
      }

      // Make sure template lists refresh wherever they are used
      if (businessAccountId) {
        queryClient.invalidateQueries({ queryKey: ['meta-templates', businessAccountId] });
      }

      if (businessAccountId && templateId) {
        queryClient.invalidateQueries({ queryKey: ['meta-template', templateId, businessAccountId] });
      }

      toast({ variant: "success",
        title: 'Template approved',
        description: `Template "${templateName}" is approved and ready to use.`,
      });
    };

    const handleNotificationCountUpdate = (payload: any) => {
      const payloadAccountId =
        payload?.businessAccountId ?? payload?.business_account_id ?? null;
      const scope = payloadAccountId || 'no-account';
      const unreadValue =
        typeof payload?.unreadCount === 'number'
          ? payload.unreadCount
          : typeof payload?.unread_count === 'number'
          ? payload.unread_count
          : undefined;

      if (typeof unreadValue !== 'number') {
        return;
      }

      mutateCache(payloadAccountId, (oldData) => {
        if (!oldData) {
          return {
            success: true,
            data: [],
            unreadCount: unreadValue,
          };
        }

        return {
          ...oldData,
          unreadCount: unreadValue,
        };
      });

      if (scope === latestScopeRef.current) {
        setUnreadCount(unreadValue);
      }
    };

    // When admin changes plan features or overrides, immediately refresh entitlements
    // so sidebar items and feature gates update in real time (like Netflix/Amazon)
    const handleEntitlementUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['organization-entitlements'] });
      queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_KEY });
    };

    // Generate notification from incoming chat messages via websocket
    // This works even when backend NotificationService fails (e.g., MySQL connection errors)
    const handleIncomingMessage = (data: any) => {
      const isInbound = data.isInbound || data.is_inbound || data.message?.is_inbound || data.message?.isInbound;
      const type = data.type || data.message?.type;

      // Only create notifications for inbound new messages
      if (!isInbound && type !== 'new_message') return;
      if (type === 'messages_read' || type === 'status_update') return;

      const msg = data.message || data;
      const conversationId = data.conversationId || msg.conversationId || msg.conversation_id;
      const messageId = data.messageId || msg.messageId || msg.id;
      const contactName = msg.contactName || msg.contact_name || msg.senderPhone || msg.sender_phone || 'Unknown';
      const content = msg.content || msg.content_text || msg.messagePreview || '';
      const businessAccountId = data.businessAccountId || msg.businessAccountId || msg.business_account_id;

      if (!conversationId) return;

      // Deduplicate: skip if we already have a notification for this message
      const dedupeId = `ws-msg-${messageId || conversationId}-${Date.now()}`;

      const wsNotification: Notification = {
        id: dedupeId,
        type: 'MESSAGE_RECEIVED',
        title: 'New Message',
        message: `New message from ${contactName}: ${content}`.substring(0, 200),
        data: { conversationId, contactName, messageId, messagePreview: content },
        isRead: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        businessAccountId: businessAccountId || null,
        organizationId: null,
        userId: user?.id,
      };

      // Only add if for current business account scope
      const targetScope = businessAccountId || 'no-account';
      if (targetScope === latestScopeRef.current) {
        setNotificationsList((prev) => {
          // Skip duplicate — if a backend notification arrived first with same conversationId + similar timestamp
          const isDuplicate = prev.some(n =>
            n.data?.conversationId === conversationId &&
            n.type === 'MESSAGE_RECEIVED' &&
            Math.abs(new Date(n.createdAt).getTime() - Date.now()) < 10000
          );
          if (isDuplicate) return prev;
          return [wsNotification, ...prev].slice(0, 50);
        });
        setUnreadCount((prev) => prev + 1);
      }
    };

    // Handle both event formats (new-message has message wrapper, conversation_updated is flat)
    const handleNewMessageForNotification = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) handleIncomingMessage({ ...msg, ...data, isInbound: true });
    };

    const handleConversationUpdatedForNotification = (data: any) => {
      if (data.type === 'new_message' && (data.isInbound || data.is_inbound)) {
        handleIncomingMessage(data);
      }
    };

    // Refresh messaging usage (msg limit count) when outbound messages are detected
    let messagingUsageDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleOutboundMessageForUsage = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) return;
      // Debounce: batch rapid outbound events into a single invalidation
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      messagingUsageDebounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messaging-usage'] });
      }, 3000);
    };

    socket.on('notification', handleNotification);
    socket.on('notification_count_update', handleNotificationCountUpdate);
    socket.on('template_status_update', handleTemplateStatusUpdate);
    socket.on('entitlement_updated', handleEntitlementUpdated);
    socket.on('new-message', handleNewMessageForNotification);
    socket.on('new-message', handleOutboundMessageForUsage);
    socket.on('conversation_updated', handleConversationUpdatedForNotification);

    return () => {
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      socket.off('notification', handleNotification);
      socket.off('notification_count_update', handleNotificationCountUpdate);
      socket.off('template_status_update', handleTemplateStatusUpdate);
      socket.off('entitlement_updated', handleEntitlementUpdated);
      socket.off('new-message', handleNewMessageForNotification);
      socket.off('new-message', handleOutboundMessageForUsage);
      socket.off('conversation_updated', handleConversationUpdatedForNotification);
    };
  }, [socket, isConnected, user, mutateCache, normalizeNotificationPayload, queryClient, toast]);

  useEffect(() => {
    if (!socket || !isConnected) {
      if (businessRoomRef.current) {
        businessRoomRef.current = null;
      }
      return;
    }

    const nextRoom = selectedAccountId ? `business-account-${selectedAccountId}` : null;

    if (businessRoomRef.current && businessRoomRef.current !== nextRoom) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    if (nextRoom && businessRoomRef.current !== nextRoom) {
      socket.emit('join_room', { roomId: nextRoom });
      businessRoomRef.current = nextRoom;
    }

    if (!nextRoom && businessRoomRef.current) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    return () => {
      if (businessRoomRef.current) {
        socket.emit('leave_room', { roomId: businessRoomRef.current });
        businessRoomRef.current = null;
      }
    };
  }, [socket, isConnected, selectedAccountId]);

  // Mark notification as read
  // Optimistic-first: update local state BEFORE awaiting the API so any overlapping
  // re-invocation (e.g., consumer useEffect re-firing during the request's latency)
  // sees the notification as already read and short-circuits.
  const markAsRead = useCallback(async (id: string) => {
    try {
      if (!user) return;

      // Skip client-side pseudo-notifications. IDs prefixed `ws-msg-` are invented
      // locally in handleIncomingMessage as a defensive fallback when the backend
      // 'notification' socket event is slow — they don't exist in the DB, so a PUT
      // would 404. We still update local state so the bell badge clears cleanly.
      if (typeof id === 'string' && id.startsWith('ws-msg-')) {
        const wasUnreadPseudo = notificationsListRef.current.some(
          (n) => n.id === id && !n.isRead
        );
        setNotificationsList((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        if (wasUnreadPseudo) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      // Short-circuit if we already know this notification is read. Prevents spurious
      // duplicate PUTs when consumer effects re-fire against the same id mid-flight.
      const existing = notificationsListRef.current.find((n) => n.id === id);
      if (existing && existing.isRead) return;
      const wasUnread = Boolean(existing && !existing.isRead);

      // Optimistic local update (runs before the network round-trip)
      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.id === id ? { ...notification, isRead: true } : notification
        )
      );

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedAccountId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) =>
            notification.id === id ? { ...notification, isRead: true } : notification
          ),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });

      // Fire the API call after the optimistic update so duplicate invocations
      // during its latency window find isRead=true in the ref and early-return.
      await notificationsApi.markAsRead(id);
    } catch (error) {
    }
  }, [mutateCache, selectedAccountId, user]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      if (!user) return;
      await notificationsApi.markAllAsRead({
        businessAccountId: selectedAccountId || undefined,
      });

      setNotificationsList((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      setUnreadCount(0);

      mutateCache(selectedAccountId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) => ({
            ...notification,
            isRead: true,
          })),
          unreadCount: 0,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedAccountId, user]);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      if (!user) return;
      await notificationsApi.delete(id);

      let removedUnread = false;
      setNotificationsList((prev) =>
        prev.filter((notification) => {
          if (notification.id === id && !notification.isRead) {
            removedUnread = true;
          }
          return notification.id !== id;
        })
      );

      if (removedUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedAccountId, (oldData) => {
        if (!oldData) return oldData;

        const notificationToDelete = oldData.data.find((notification) => notification.id === id);
        const wasUnread = notificationToDelete ? !notificationToDelete.isRead : false;

        return {
          ...oldData,
          data: oldData.data.filter((notification) => notification.id !== id),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedAccountId, user]);

  // Bulk-mark every MESSAGE_RECEIVED notification for a conversation as read LOCALLY
  // (no API calls). Pairs with UnreadMessagesContext.markConversationAsRead — that one
  // fires the server-side bulk mark via MessageController, which emits
  // notification_count_update. This method just syncs the local notification list so
  // MessageArea doesn't have to loop N individual markAsRead PUTs for notifications
  // that were already bulk-marked server-side.
  const markConversationAsReadLocal = useCallback((conversationId: string) => {
    if (!conversationId) return;

    const matching = notificationsListRef.current.filter(
      (n) =>
        !n.isRead &&
        n.type === 'MESSAGE_RECEIVED' &&
        (n.data as any)?.conversationId === conversationId
    );
    if (matching.length === 0) return;

    const matchingSet = new Set(matching.map((n) => n.id));

    setNotificationsList((prev) =>
      prev.map((n) => (matchingSet.has(n.id) ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - matching.length));

    mutateCache(selectedAccountId, (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        data: oldData.data.map((n) =>
          matchingSet.has(n.id) ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, (oldData.unreadCount || 0) - matching.length),
      };
    });
  }, [mutateCache, selectedAccountId]);

  const value = useMemo<NotificationContextType>(() => ({
    notifications: notificationsList,
    unreadCount,
    isLoading,
    error: error as Error | null,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    markConversationAsReadLocal,
    refetch,
  }), [notificationsList, unreadCount, isLoading, error, markAsRead, markAllAsRead, deleteNotification, markConversationAsReadLocal, refetch]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
